import useSession from '@/hooks/useSession';
import {
  ThreadID,
  ThreadItem,
  ThreadProcessingStatus,
  ThreadStatus,
} from '@/models/thread';
import { useQuery } from '@tanstack/react-query';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';

const useAsyncThreadUpdate = ({
  threadID,
  setItems,
  routeParamID,
  processingStatus,
  updateThreadProcessing,
  setStatus,
}: {
  threadID: ThreadID;
  setItems: Dispatch<SetStateAction<ThreadItem[]>>;
  routeParamID: string | undefined;
  processingStatus: ThreadProcessingStatus | undefined;
  updateThreadProcessing: (
    processingStatus: ThreadProcessingStatus | undefined
  ) => void;
  setStatus: Dispatch<SetStateAction<ThreadStatus>>;
}): {
  shouldCheckAsync: boolean;
  setShouldCheckAsync: Dispatch<SetStateAction<boolean>>;
} => {
  const [shouldCheckAsync, setShouldCheckAsync] = useState(false);
  const { session } = useSession();
  const { threads } = session;

  useOnLeavingThread({
    processingStatus,
    routeParamID,
    threadID,
    updateThreadProcessing,
  });

  useQuery({
    queryKey: [
      'async-processing-thread-state-update',
      threadID,
      processingStatus,
    ],
    queryFn: () => {
      const threadIndex = threads.findIndex((thread) => thread.id === threadID);

      if (threadIndex === -1) {
        return false;
      }
      const thread = threads[threadIndex];
      const lastItem = thread.items[thread.items.length - 1];
      if (lastItem.response.length > 0) {
        setItems(thread.items);
        setShouldCheckAsync(false);
        updateThreadProcessing(undefined);
        setStatus('READY');
      }

      return null;
    },
    enabled: shouldCheckAsync && typeof processingStatus === 'undefined',
  });

  return {
    shouldCheckAsync,
    setShouldCheckAsync,
  };
};

const useOnLeavingThread = ({
  processingStatus,
  routeParamID,
  threadID,
  updateThreadProcessing,
}: {
  processingStatus: ThreadProcessingStatus | undefined;
  updateThreadProcessing: (
    processingStatus: ThreadProcessingStatus | undefined
  ) => void;
  routeParamID: string | undefined;
  threadID: ThreadID;
}) => {
  const { pushUserEvent } = useSession();
  useEffect(() => {
    return () => {
      if (
        processingStatus &&
        routeParamID !== '' &&
        routeParamID !== 'new' &&
        routeParamID !== threadID
      ) {
        pushUserEvent('left_processing_thread');
        updateThreadProcessing(ThreadProcessingStatus.ASYNC);
      }
    };
  }, [
    processingStatus,
    routeParamID,
    updateThreadProcessing,
    threadID,
    pushUserEvent,
  ]);
};

export default useAsyncThreadUpdate;
